<template>
  <div id="margin-breakdown-history" style="background-color:#fff;">
    <div style="padding: 20px;">
      <h3>{{ time }} -- {{ user }}</h3>
    </div>
    <Form class="text-align-right" inline>
      <FormItem :label="$t('quote.margin_setting.overall_margin')">
        <InputNumber readonly v-model="summary.overall_margin" style="width: auto;"
                     :formatter="overallMarginFormat"></InputNumber>
      </FormItem>
    </Form>

    <div class="summary-preview" style="margin-bottom: 24px;">
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">
          {{ $t('quote.margin_setting.total_cost') }}
          <Tooltip max-width="200" transfer :content="$t('quote.margin_setting.summary_total_cost_help')">
            <Icon type="ios-help-circle"/>
          </Tooltip>
        </Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, summary.total_cost, 2, 2) }}</Col>
      </Row>
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">
          {{ $t('quote.margin_setting.expenses.company_expenses') }}
        </Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, cost.company_expenses, 2, 2) }}</Col>
      </Row>
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">
          {{ $t('quote.margin_setting.expenses.qc_cost') }}
        </Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, cost.qc_cost, 2, 2) }}</Col>
      </Row>
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">
          {{ $t('quote.margin_setting.gross_profit') }}
          <Tooltip max-width="200" transfer :content="$t('quote.margin_setting.summary_gross_profit_help')">
            <Icon type="ios-help-circle"/>
          </Tooltip>
        </Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, summary.profit, 2, 2) }}</Col>
      </Row>
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">
          {{ $t('quote.margin_setting.tax') }}
          <Tooltip max-width="200" transfer :content="$t('quote.margin_setting.summary_tax_help')">
            <Icon type="ios-help-circle"/>
          </Tooltip>
        </Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, summary.total_tax, 2, 2) }}</Col>
      </Row>
      <Row :gutter="32" type="flex" justify="start">
        <Col span="4" class="title text-align-right">{{ $t('quote.margin_setting.total') }}</Col>
        <Col span="4" class="value">{{ intlCurrencyNumber(this.currency, summary.amount, 2, 2) }}</Col>
        <Col span="6" offset="10">
          <p style="font-weight: bold;margin-bottom: 5px;">{{ $t('quote.margin_setting.currencyTitle') }} ( {{
            $t('quote.info.exchange_rate') }} {{ exchangeRate }} )</p>
          <Select v-model="currency" style="width:200px" @on-change="handleCurrencyChange">
            <Option v-for="item in currencyList" :value="item.value" :key="item.value">{{ item.label }}</Option>
          </Select>
        </Col>
      </Row>
    </div>

    <div class="margin-setting-form">
      <h2>{{ $t('quote.margin_setting.margin_setting_breakdown') }}</h2>
      <Form name="marginSettingForm">
        <Row>
          <Col span="24">
            <vxe-table
              border
              ref="xTable"
              show-footer
              resizable
              highlight-current-row
              highlight-hover-row
              :span-method="spanMethod"
              :footer-method="footerMethod"
              footer-row-class-name="footerCellClass"
              :scroll-y="{gt: -1}"
              :data="marginBreakoutTableData">
              <vxe-table-column type="seq" width="40"></vxe-table-column>
              <vxe-table-column field="item_group"
                                :title="$t('quote.margin_setting.table_col_category')"
                                width="120">
              </vxe-table-column>
              <vxe-table-column field="item_name"
                                :title="$t('quote.margin_setting.table_col_subcategory')"
                                width="160">
              </vxe-table-column>
              <vxe-table-column :title="$t('quote.margin_setting.table_col_cost')"
                                width="160">
                <template v-slot="{ row }">
                  <div style="display: flex; justify-content: space-between; align-items: center">
                    <span>{{ intlCurrencyNumber(currency, row.cost, 2, 2) }}</span>
                    <Button type="text" @click="handleShowMarginBreakDetail(row)" icon="md-eye"></Button>
                  </div>
                </template>
              </vxe-table-column>
              <vxe-table-column
                width="120"
                :title="$t('quote.margin_setting.table_col_margin')">
                <template v-slot="{ row }">
                  <span>{{ intlCurrencyNumber(currency, grossProfit(row), 2, 2) }} </span>
                </template>
              </vxe-table-column>
              <vxe-table-column
                :title="$t('quote.margin_setting.table_col_price')"
                width="140">
                <template v-slot="{ row }">
                  <span>{{ intlCurrencyNumber(currency, price(row), 2, 2) }} </span>
                </template>
              </vxe-table-column>
              <vxe-table-column field="margin"
                                width="120"
                                :title="$t('quote.margin_setting.table_col_margin_percent')">
              </vxe-table-column>
              <vxe-table-column
                width="120"
                :title="$t('quote.margin_setting.table_col_unit_cost')">
                <template v-slot="{ row }">
                  <span>{{ intlCurrencyNumber(currency, unitCost(row), 2, 2) }} </span>
                </template>
              </vxe-table-column>
              <vxe-table-column
                width="140"
                :title="$t('quote.margin_setting.table_col_unit_cost_taxed')">
                <template v-slot="{ row }">
                  <span>{{ intlCurrencyNumber(currency, unitCostTaxed(row), 2, 2) }} </span>
                </template>
              </vxe-table-column>
              <vxe-table-column field="remark"
                                min-width="200"
                                :title="$t('quote.margin_setting.table_col_remark')">
              </vxe-table-column>
            </vxe-table>
          </Col>
        </Row>
      </Form>
      <Modal
        v-model="showMarginBreakDetail"
        title=""
        width="70%"
        footer-hide
        :transfer="false"
      >
        <MarginSettingDetailForm :detailData="detailData" :currency="currency"
                                 :loading="loading"></MarginSettingDetailForm>
      </Modal>
    </div>
  </div>
</template>

<script>
import XEUtils from 'xe-utils'
import { mapActions } from 'vuex'
import { intlCurrencyNumber, currencyConvert } from '@/libs/util'
import MarginSettingDetailForm from './margin-setting-detail-form'
import CONFIG from '@/config'

export default {
  name: 'margin-breakdown-history-form',
  components: {
    MarginSettingDetailForm
  },
  mounted () {
    this.loadMarginSnapshotsById()
  },
  data () {
    return {
      cost: {},
      summary: {},
      marginBreakoutTableData: [],
      time: '',
      user: '',
      currency: 'CNY',
      quantity: 0,
      exchangeRate: CONFIG.defaultChangeRate,
      grossProfitSum: 0,
      priceSum: 0,
      showMarginBreakDetail: false,
      detailData: [],
      loading: true,
      currencyList: [
        { label: 'CNY', value: 'CNY' },
        { label: 'USD', value: 'USD' }
      ]
    }
  },
  computed: {
    quoteId () {
      return this.$route.params.quoteId
    },
    historyId () {
      return this.$route.params.historyId
    }
  },
  methods: {
    ...mapActions([
      'getMarginSnapshotsById',
      'getMarginBreakDetail'
    ]),
    intlCurrencyNumber,
    currencyConvert,
    loadMarginSnapshotsById () {
      this.getMarginSnapshotsById({ quoteId: this.quoteId, snapshotId: this.historyId }).then(res => {
        if (res.data) {
          this.cost = res.data.cost_summary
          this.summary = res.data.margin_summary
          this.marginBreakoutTableData = res.data.margin_breakdowns.data
          this.currency = res.data.quote.currency
          this.quantity = res.data.quote.quantity
          this.exchangeRate = res.data.quote.exchange_rate
          this.time = res.data.created_at
          this.user = res.data.user.name
          if (this.marginBreakoutTableData.length > 0) {
            this.marginBreakoutTableData.forEach((item) => {
              if (res.data.margin_breakdowns.currency !== this.currency) {
                item.cost = this.currencyConvert(this.currency, item.cost, this.exchangeRate)
                item.tax = this.currencyConvert(this.currency, item.tax, this.exchangeRate)
              }
              item.margin = XEUtils.round(XEUtils.multiply(item.margin, 100), 2)
            })
            this.$refs.xTable.loadData(this.marginBreakoutTableData)
          }
          if (this.summary.currency !== this.currency) {
            this.summary.total_cost = this.currencyConvert(this.currency, this.summary.total_cost, this.exchangeRate)
            this.summary.profit = this.currencyConvert(this.currency, this.summary.profit, this.exchangeRate)
            this.summary.total_tax = this.currencyConvert(this.currency, this.summary.total_tax, this.exchangeRate)
            this.summary.amount = this.currencyConvert(this.currency, this.summary.amount, this.exchangeRate)
          }
          if (this.cost.currency !== this.currency) {
            this.cost.qc_cost = this.currencyConvert(this.currency, this.cost.qc_cost, this.exchangeRate)
            this.cost.company_expenses = this.currencyConvert(this.currency, this.cost.company_expenses, this.exchangeRate)
          }
        }
      })
    },
    spanMethod ({ row, rowIndex, $rowIndex, column, columnIndex, $columnIndex, data }) {
      const fields = ['item_group']
      const cellValue = XEUtils.get(row, column.property)

      if (cellValue && fields.includes(column.property)) {
        const prevRow = data[rowIndex - 1]
        let nextRow = data[rowIndex]

        if (prevRow && XEUtils.get(prevRow, column.property) === cellValue) {
          return { rowspan: 0, colspan: 0 }
        } else {
          let countRowspan = 0

          while (nextRow && XEUtils.get(nextRow, column.property) === cellValue) {
            nextRow = data[++countRowspan + rowIndex]
          }

          if (countRowspan > 1) {
            return { rowspan: countRowspan, colspan: 1 }
          }
        }
      }
    },
    footerMethod ({ columns, columnIndex, data }) {
      const footerData = [
        columns.map((column, columnIndex) => {
          if (columnIndex === 2) {
            return 'Overall'
          }

          if (columnIndex === 3) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => row.cost)), 2, 2)
          }
          if (columnIndex === 4) {
            this.grossProfitSum = XEUtils.sum(data.map(row => this.grossProfit(row)))
            return this.intlCurrencyNumber(this.currency, this.grossProfitSum, 2, 2)
          }
          if (columnIndex === 5) {
            this.priceSum = XEUtils.sum(data.map(row => this.price(row)))
            return this.intlCurrencyNumber(this.currency, this.priceSum, 2, 2)
          }
          if (columnIndex === 6) {
            return XEUtils.round(XEUtils.divide(this.grossProfitSum, this.priceSum) * 100, 2)
          }
          if (columnIndex === 7) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitCost(row))), 2, 2)
          }
          if (columnIndex === 8) {
            return this.intlCurrencyNumber(this.currency, XEUtils.sum(data.map(row => this.unitCostTaxed(row))), 2, 2)
          }
          return null
        })
      ]
      return footerData
    },
    grossProfit (row) {
      return XEUtils.round(XEUtils.multiply(row.cost, row.margin / 100), 5)
    },
    price (row) {
      return XEUtils.round(XEUtils.multiply(row.cost, row.margin / 100 + 1), 5)
    },
    unitCost (row) {
      return XEUtils.round(XEUtils.multiply(row.cost, row.margin / 100 + 1) / this.quantity, 5)
    },
    unitCostTaxed (row) {
      return XEUtils.round((XEUtils.multiply(row.cost, row.margin / 100 + 1) + row.tax) / this.quantity, 5)
    },
    overallMarginFormat (value) {
      return XEUtils.round(XEUtils.multiply(value, 100), 2) + '%'
    },
    handleShowMarginBreakDetail (row) {
      this.detailData = []
      this.loading = true
      if (row && row.report_name) {
        this.getMarginBreakDetail({ quoteId: this.quoteId, reportName: row.report_name }).then(resp => {
          this.detailData = resp.data.data
          this.loading = false
          if ((resp.data.currency !== this.currency) && this.detailData.length > 0) {
            this.detailData.forEach((item) => {
              item.price = this.currencyConvert(this.currency, item.price, this.exchangeRate)
              item.price_amount = this.currencyConvert(this.currency, item.price_amount, this.exchangeRate)
              item.vat_amount = this.currencyConvert(this.currency, item.vat_amount, this.exchangeRate)
            })
          }
        })
      }
      this.showMarginBreakDetail = true
    },
    handleCurrencyChange (currency) {
      if (this.marginBreakoutTableData.length > 0) {
        this.marginBreakoutTableData.forEach((item) => {
          item.cost = this.currencyConvert(currency, item.cost, this.exchangeRate)
          item.tax = this.currencyConvert(currency, item.tax, this.exchangeRate)
        })
        this.$refs.xTable.loadData(this.marginBreakoutTableData)
      }
      if (this.summary) {
        this.summary.total_cost = this.currencyConvert(currency, this.summary.total_cost, this.exchangeRate)
        this.summary.profit = this.currencyConvert(currency, this.summary.profit, this.exchangeRate)
        this.summary.total_tax = this.currencyConvert(currency, this.summary.total_tax, this.exchangeRate)
        this.summary.amount = this.currencyConvert(currency, this.summary.amount, this.exchangeRate)
      }
      if (this.cost) {
        this.cost.qc_cost = this.currencyConvert(currency, this.cost.qc_cost, this.exchangeRate)
        this.cost.company_expenses = this.currencyConvert(currency, this.cost.company_expenses, this.exchangeRate)
      }
    }
  }
}
</script>

<style lang="less">
  #margin-breakdown-history {
    .title {
      background-color: #e7e7e7;
      border-bottom: 1px solid #f7f7f7;
    }
    .summary-preview { padding: 0 15px; }
    .margin-setting-form {
      margin-top: 20px;
      padding-bottom: 20px;
    }
    .footerCellClass {
      background-color: #F8F8F8;
      font-weight: bold;
    }
  }
</style>
